/**
 * @file zapierCopy.ts
 * @brief Words for Zapier (Z1, ADR-060): the company switch and the consent
 *        screen's "can / cannot" lists, built from the scopes asked for.
 *
 * @details
 * DES 27-10-2026: the consent screen showed Claude's text to Zapier ("Read
 * meeting transcripts", "Change, delete or share anything"), which is not
 * true for Zapier: it can create notes, and sends a transcript only when the
 * Zap ticks "Include transcript". So the Zapier lists come from the scopes
 * in the request; an unknown scope is shown as it is, never hidden.
 * The MCP (Claude, ChatGPT) text stays exactly as it was.
 *
 * M1 (29-10-2026): Make gets the same words with "scenario" for "Zap"; its
 * own company switch (`make`) and Connect link (`MAKE_APP_URL`).
 *
 * Pure (no React, no "@/" imports) so node:test can load it.
 *
 * @references
 * - BACKEND/src/features/mcp/oauth.routes.mjs (POST /oauth/request: kind, scopes)
 * - BACKEND/src/features/zapier/zapierAuth.mjs (the four Zapier scopes)
 *
 * @author DES (prepared with Claude)
 * @date 27-10-2026
 */

/** Company → Features and Settings → Integrations, same line in both. */
export const ZAPIER_SWITCH_TEXT =
  "Send meeting minutes, to-dos and notes to 9,000+ apps through Zapier. Off by default; when off, nobody in the company can connect Zapier.";

/** What a member sees instead of Connect while the company switch is off. */
export const ZAPIER_ASK_ADMIN_TEXT =
  "Your company admin has not allowed Zapier. Ask your company admin to turn it on in Company → Features.";

export const ZAPIER_CONNECT_TEXT = "Connect VERBLIZR to 9,000+ apps with Zapier";

/** Ideas shown on the Zapier card (W31): what goes where. */
export const ZAPIER_IDEAS: [string, string][] = [
  ["Minutes", "Slack channel"],
  ["Notes", "Notion page"],
  ["To-dos", "Trello / Todoist"],
  ["Every meeting", "Google Sheets row"],
];

/** M1: Make, same shape as the Zapier lines. App count = make.com's own (DES to confirm). */
export const MAKE_SWITCH_TEXT =
  "Send meeting minutes, to-dos and notes to 3,000+ apps through Make. Off by default; when off, nobody in the company can connect Make.";

export const MAKE_ASK_ADMIN_TEXT =
  "Your company admin has not allowed Make. Ask your company admin to turn it on in Company → Features.";

export const MAKE_CONNECT_TEXT = "Connect VERBLIZR to 3,000+ apps with Make";

export const MAKE_IDEAS: [string, string][] = [
  ["Minutes", "Microsoft Teams"],
  ["To-dos", "monday.com / ClickUp"],
  ["Notes", "Google Docs"],
  ["Every meeting", "Airtable record"],
];

/** Automation apps (Zapier, Make): connections listed under Integrations, not as AI apps. */
export const AUTOMATION_KINDS = ["zapier", "make"] as const;
export type AutomationKind = (typeof AUTOMATION_KINDS)[number];
export const isAutomationKind = (kind: string | undefined): kind is AutomationKind =>
  (AUTOMATION_KINDS as readonly string[]).includes(kind ?? "");

/** What one automation workflow is called in that app. */
const FLOW_WORD: Record<AutomationKind, { many: string; transcript: string }> = {
  zapier: { many: "Zaps", transcript: "Send a transcript only when a Zap ticks “Include transcript”" },
  make: { many: "scenarios", transcript: "Send a transcript only when a scenario turns on “Include transcript”" },
};

/** The MCP lists, unchanged since 06-10-2026. */
export const MCP_CAN = [
  "Search your meetings and read their summaries, decisions and action items",
  "Read meeting transcripts",
  "Only meetings you can already open in Verblizr",
];

export const MCP_CANNOT = [
  "Change, delete or share anything",
  "Listen to recordings or see billing",
  "See medical assessments or interpretation sessions",
];

/** One line per Zapier scope, in this order. */
const ZAPIER_SCOPE_LINES: Record<string, string> = {
  "read:meetings": "Read the minutes and decisions of meetings you can open",
  "read:actions": "Read your to-dos from meetings",
  "read:notes": "Read your notes",
  "write:notes": "Create notes in your account",
};

export type ConsentCopy = {
  title: string;
  can: string[];
  cannot: string[];
  footer: string;
};

export type ConsentKind = "mcp" | AutomationKind | string;

/**
 * The consent screen's words for this request. `kind` missing (a backend
 * before Z1) reads as MCP, the only client there was.
 */
export function consentCopy(appName: string, kind: ConsentKind | undefined, scopes: string[] = []): ConsentCopy {
  if (!isAutomationKind(kind)) {
    return {
      title: `${appName} wants to read your Verblizr meetings`,
      can: MCP_CAN,
      cannot: MCP_CANNOT,
      footer: `What you share goes to ${appName}, and its own terms apply. You can disconnect it at any time from Profile → Connected AI apps.`,
    };
  }

  const asked = Array.from(new Set(scopes.map((s) => s.trim()).filter(Boolean)));
  const known = Object.keys(ZAPIER_SCOPE_LINES).filter((s) => asked.includes(s));
  const unknown = asked.filter((s) => !(s in ZAPIER_SCOPE_LINES));

  const can = known.map((s) => ZAPIER_SCOPE_LINES[s]);
  for (const s of unknown) can.push(`Use the permission “${s}”`);
  if (asked.includes("read:meetings") || asked.includes("read:notes")) {
    can.push(FLOW_WORD[kind].transcript);
  }

  const writes = asked.includes("write:notes") || unknown.some((s) => s.startsWith("write:"));
  const cannot = [
    writes ? "Change or delete anything, or share it with other people" : "Create, change, delete or share anything",
    "Listen to recordings or see billing",
    "See medical assessments or interpretation sessions",
  ];

  return {
    title: `${appName} wants to use your Verblizr account`,
    can,
    cannot,
    footer: `What you send goes to ${appName} and to the apps you connect in your ${FLOW_WORD[kind].many}, and their own terms apply. You can disconnect it at any time from Profile → Connected apps.`,
  };
}
